import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { PauseCircle, Clock, AlertTriangle, Users, PlayCircle } from 'lucide-react';

function ParkedBillCard({ bill, onResume }) {
  return (
    <div className="parked-bill-card" onClick={onResume}>
      <div className="flex justify-between">
        <div>
          <p className="font-medium text-sm">{bill.patient_name || 'Walk-in'}</p>
          <p className="text-xs text-amber-700">{bill.items.length} items</p>
        </div>
        <PlayCircle className="w-5 h-5 text-amber-600" />
      </div>
    </div>
  );
}

function ExpiringItem({ item }) {
  return (
    <div className="px-4 py-2 flex justify-between text-sm">
      <div>
        <p className="font-medium">{item.brand_name}</p>
        <p className="text-xs text-slate-500">{item.batch_id}</p>
      </div>
      <div className="text-right">
        <Badge className="badge-near-expiry text-xs">{item.days_to_expiry}d</Badge>
        <p className="text-xs text-slate-500">Qty: {item.quantity}</p>
      </div>
    </div>
  );
}

function LowStockItem({ item }) {
  return (
    <div className="px-4 py-2 flex justify-between text-sm">
      <p className="font-medium">{item.brand_name}</p>
      <div className="text-right">
        <Badge className="badge-low-stock text-xs">Stock: {item.total_stock}</Badge>
        <p className="text-xs text-slate-500">Min: {item.min_stock_level}</p>
      </div>
    </div>
  );
}

export function DashboardSidebar({ parkedBills, expiringMeds, lowStockMeds, stats, onResumeBill }) {
  const parkedElems = [];
  for (let i = 0; i < Math.min(parkedBills.length, 3); i++) {
    const bill = parkedBills[i];
    parkedElems.push(<ParkedBillCard key={bill.id} bill={bill} onResume={function() { onResumeBill(bill); }} />);
  }

  const expiringElems = [];
  for (let i = 0; i < expiringMeds.length; i++) {
    expiringElems.push(<ExpiringItem key={i} item={expiringMeds[i]} />);
  }

  const lowStockElems = [];
  for (let i = 0; i < lowStockMeds.length; i++) {
    lowStockElems.push(<LowStockItem key={i} item={lowStockMeds[i]} />);
  }

  return (
    <div className="col-span-12 lg:col-span-5 space-y-4">
      {parkedBills.length > 0 && (
        <Card>
          <CardHeader className="py-2 px-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <PauseCircle className="w-4 h-4 text-amber-500" />Parked ({parkedBills.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-2">{parkedElems}</CardContent>
        </Card>
      )}

      <Card className="border-red-200">
        <CardHeader className="py-2 px-4 bg-red-50 rounded-t-lg">
          <CardTitle className="text-sm flex items-center gap-2 text-red-800">
            <Clock className="w-4 h-4" />Expiring ({expiringMeds.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-32">
            {expiringElems.length === 0
              ? <p className="p-4 text-center text-sm text-slate-400">None</p>
              : <div className="divide-y">{expiringElems}</div>}
          </ScrollArea>
        </CardContent>
      </Card>

      <Card className="border-amber-200">
        <CardHeader className="py-2 px-4 bg-amber-50 rounded-t-lg">
          <CardTitle className="text-sm flex items-center gap-2 text-amber-800">
            <AlertTriangle className="w-4 h-4" />Low Stock ({lowStockMeds.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-32">
            {lowStockElems.length === 0
              ? <p className="p-4 text-center text-sm text-slate-400">OK</p>
              : <div className="divide-y">{lowStockElems}</div>}
          </ScrollArea>
        </CardContent>
      </Card>

      {stats && (
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-navy-600" />
                <div>
                  <p className="text-sm font-medium">Customers</p>
                  <p className="text-xs text-slate-500">{stats.customers_this_month} / {stats.max_customers_per_month === Infinity ? 'Inf' : stats.max_customers_per_month}</p>
                </div>
              </div>
              <Badge className={'plan-badge ' + (stats.plan === 'free_trial' ? 'trial' : 'pro')}>
                {stats.plan === 'free_trial' ? 'Trial' : 'Pro'}
              </Badge>
            </div>
            {stats.trial_ends_at && <p className="text-xs text-amber-600 mt-2">Trial: {new Date(stats.trial_ends_at).toLocaleDateString()}</p>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
